import React from "react";
import { ListTableColumn, createListTableColumns, ensure, Stack, Text, Link, depascalize } from "sprint-common-web";

import { AssetTypeBadge } from "../../../../common/components/AssetTypeBadge";
import { RecordTypeBadge } from "../../../../common/components/RecordTypeBadge";
import { DynamicSubtitleType } from "../../../../common/types/DynamicSubtitleType";
import { getLanguageDisplayName } from "../../../../common/types/Language";
import { NoteSubjectType } from "../../../../common/types/NoteSubjectType";
import { RecordType } from "../../../../common/types/RecordType";
import { renderNotes } from "../../../../common/utils/renderNotes";
import { LanguageAssetInfo } from "../../../common/components/listings/AssetsAvailabilityList";
import { VideoInfo } from "../../../common/components/listings/VideoInfo";
import { OrderAssetAvailability } from "../../../common/types/OrderAssetAvailability";
import { stringifyOrderResolutionDimensions } from "../../../common/types/OrderResolutionDimensions";
import { renderNoteBadge } from "../../../common/utils/renderNoteBadge";
import { AudioFileType, VideoFileType } from "../../../orders/dashboard/OrderDashboardItem";
import {
    OrderRequestAssetItem,
    OrderRequestAudioAssetItem,
    OrderRequestSubtitleAssetItem,
    OrderRequestVideoAssetItem
} from "./OrderRequestAssetItem";

const COLUMN_WIDTHS = {
    recordType: { min: 80, max: 90 },
    storageType: { min: 80, max: 90 },
    name: { min: 100, max: 280 },
    availability: { min: 80, max: 80 },
    assetId: { min: 80, max: 180 },
    filePath: { min: 70, max: 350 },
    fileType: { min: 40, max: 100 }
} as const;

export const createOrderRequestAssetsSectionColumns = ({
    areNotesLoading
}: {
    areNotesLoading?: boolean;
}): ListTableColumn<OrderRequestAssetItem>[] => {
    return createListTableColumns<OrderRequestAssetItem>([
        {
            type: "basic",
            key: "recordType",
            name: "Record type",
            minWidth: COLUMN_WIDTHS.recordType.min,
            maxWidth: COLUMN_WIDTHS.recordType.max,
            onRender: renderRecordType
        },
        {
            type: "basic",
            key: "storageType",
            name: "Asset type",
            minWidth: COLUMN_WIDTHS.storageType.min,
            maxWidth: COLUMN_WIDTHS.storageType.max,
            onRender: renderAssetStorageType
        },
        {
            type: "basic",
            key: "name",
            name: "Name",
            minWidth: COLUMN_WIDTHS.name.min,
            maxWidth: COLUMN_WIDTHS.name.max,
            onRender: renderName
        },
        {
            type: "basic",
            key: "availability",
            name: "Availability",
            minWidth: COLUMN_WIDTHS.availability.min,
            maxWidth: COLUMN_WIDTHS.availability.max,
            onRender: renderAvailability
        },
        {
            type: "basic",
            key: "filePath",
            name: "Path",
            minWidth: 70,
            maxWidth: 350,
            onRender: renderFilePath
        },
        {
            type: "basic",
            key: "fileType",
            name: "File type",
            minWidth: 40,
            maxWidth: 100,
            onRender: renderFileType
        },
        {
            type: "basic",
            key: "assetId",
            name: "Details",
            minWidth: COLUMN_WIDTHS.assetId.min,
            maxWidth: COLUMN_WIDTHS.assetId.max,
            onRender: renderDetails
        },
        {
            type: "basic",
            key: "notes",
            name: "Notes",
            minWidth: 120,
            maxWidth: 120,
            isResizable: false,
            onRender: (item) =>
                renderNotes({
                    subjectType: NoteSubjectType.CloudAsset,
                    notes: item?.notes ?? [],
                    itemId: item?.assetId,
                    renderBadge: (noteItem) => renderNoteBadge(noteItem),
                    isLoading: areNotesLoading
                })
        }
    ]);
};

const renderAssetStorageType = (item?: OrderRequestAssetItem): JSX.Element => {
    ensure(item);

    return <AssetTypeBadge value={item.storageType} />;
};

const renderRecordType = (item?: OrderRequestAssetItem): JSX.Element => {
    ensure(item);

    return <RecordTypeBadge value={item.recordType} />;
};

const renderFilePath = (item?: OrderRequestAssetItem): JSX.Element => {
    ensure(item);

    return <Text variant="small" tooltip={item.filePath} value={item.filePath} />;
};

const renderFileType = (item?: OrderRequestAssetItem): JSX.Element => {
    ensure(item);

    switch (item.recordType) {
        case RecordType.Video:
            return <Text variant="small" value={item.fileType ? depascalize(VideoFileType[item.fileType]) : "-"} />;

        case RecordType.Audio:
            return <Text variant="small" value={item.fileType ? depascalize(AudioFileType[item.fileType]) : "-"} />;

        case RecordType.Subtitle:
            return (
                <Text variant="small" value={item.fileType ? depascalize(DynamicSubtitleType[item.fileType]) : "-"} />
            );

        default:
            return <Text variant="small" value="-" />;
    }
};

const createAssetLink = (item: OrderRequestAssetItem, children: React.ReactNode): JSX.Element => {
    const href =
        item.assetHref && item.availability === OrderAssetAvailability.FullMatch ? "/#" + item.assetHref : undefined;

    return (
        <Link href={href} disabled={!href}>
            {children}
        </Link>
    );
};

const renderVideoName = (item: OrderRequestAssetItem): JSX.Element =>
    createAssetLink(
        item,
        <Text tooltip={item.name} variant="small">
            {item.name}
        </Text>
    );

const renderLanguageName = (item: OrderRequestAssetItem): JSX.Element => {
    const languageItem = item as OrderRequestAudioAssetItem | OrderRequestSubtitleAssetItem;

    return createAssetLink(
        item,
        <Text
            tooltip={item.name}
            variant="small"
            key={item.assetId}
            value={getLanguageDisplayName(languageItem.language)}
        />
    );
};

const renderName = (item?: OrderRequestAssetItem): JSX.Element | null => {
    ensure(item);

    return item.recordType === RecordType.Video ? renderVideoName(item) : renderLanguageName(item);
};

const renderVideoAvailability = (item: OrderRequestVideoAssetItem): JSX.Element => {
    return (
        <VideoInfo
            key={`video-${item.assetId}`}
            availability={item.availability}
            requestedAspectRatio={item.aspectRatio}
            type={item.storageType}
            nonMatchingVideos={item.nonMatchingVideos}
            requestedResolution={item.targetResolution}
            testId="availability-indicator"
        />
    );
};

const renderLanguageAvailability = (item: OrderRequestAssetItem): JSX.Element => {
    const languageItem = item as OrderRequestAudioAssetItem | OrderRequestSubtitleAssetItem;

    return (
        <LanguageAssetInfo
            assetType={item.recordType}
            item={{ availability: item.availability, language: languageItem.language }}
            storageType={item.storageType}
            testId="availability-indicator"
        />
    );
};

const renderAvailability = (item?: OrderRequestAssetItem): JSX.Element | null => {
    ensure(item);

    switch (item.recordType) {
        case RecordType.Video:
            return renderVideoAvailability(item);
        case RecordType.Audio:
        case RecordType.Subtitle:
            return renderLanguageAvailability(item);
        default:
            throw new Error("Unsupported item type");
    }
};

const hasVideoDetails = (item: OrderRequestVideoAssetItem): boolean =>
    !!(item.subtitle || item.aspectRatio || item.sourceResolution);

const renderVideoDetails = (item: OrderRequestVideoAssetItem): JSX.Element => (
    <Stack>
        {item.subtitle && <Text variant="small">{getLanguageDisplayName(item.subtitle)}</Text>}
        {item.aspectRatio && <Text variant="small">{item.aspectRatio.name}</Text>}
        {item.sourceResolution && (
            <Text variant="small">{stringifyOrderResolutionDimensions(item.sourceResolution)}</Text>
        )}
    </Stack>
);

const renderDetails = (item?: OrderRequestAssetItem): JSX.Element => {
    ensure(item);

    if (item.recordType !== RecordType.Video) {
        return <Text>-</Text>;
    }

    const videoItem = item;

    if (!hasVideoDetails(videoItem)) {
        return <Text>-</Text>;
    }

    return renderVideoDetails(videoItem);
};
